import { yupResolver } from '@hookform/resolvers/yup';
import { useAppSelector } from 'app/hooks';
import { AxiosError } from 'axios';
import AlertModal from 'components/common/AlertModal';
import FeatureTitleBar from 'components/common/FeatureTitleBar';
import useGlobalModal from 'components/common/GlobalModal/useGlobalModal';
import { selectIsEditMode } from 'features/common/editModeSlice';
import importDataService from 'features/ImportData/importDataService';
import { useEffect } from 'react';
import { Button, Col, Form, Modal, Row, Table } from 'react-bootstrap';
import { useFieldArray, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { STATUS_SUCCESS, displayError, handleServiceError } from 'utils';
import * as yup from 'yup';
import { CounterPattern } from '../../models';

export interface ChangeRegistrationPatternNameModalProps {
  onClose?: () => void;
}
type FormValues = {
  counterPatterns: CounterPattern[];
};
function ChangeRegistrationPatternNameModal({ onClose }: ChangeRegistrationPatternNameModalProps) {
  const { t } = useTranslation();
  const { showGlobalModal } = useGlobalModal();
  // selector
  const isEditMode = useAppSelector(selectIsEditMode);

  // yup setup
  const schema = yup.object({
    counterPatterns: yup.array(
      yup.object({
        id: yup.number(),
        cd: yup.string(),
        name: yup.string().required(t('SaleTypeDetail.warn_field_required')),
      })
    ),
  });
  // form setup
  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: yupResolver(schema),
    defaultValues: {
      counterPatterns: [],
    },
    mode: 'onSubmit',
  });

  useEffect(() => {
    (async () => {
      // TODO: add try/catch
      const response = await importDataService.get();
      if (response.status === STATUS_SUCCESS) {
        reset({ counterPatterns: response.data });
      }
    })();
  }, [reset]);

  const { fields } = useFieldArray({
    name: 'counterPatterns',
    control,
  });

  const handleClose = () => {
    onClose && onClose();
  };

  const onSubmit = async (data: FormValues) => {
    try {
      const response = await importDataService.update(data.counterPatterns);
      showGlobalModal(() => <AlertModal title={t('App.info')} message={response.message} onClose={handleClose} />);
    } catch (e) {
      if (!handleServiceError(showGlobalModal, e as AxiosError)) {
        displayError(showGlobalModal, e as AxiosError);
      }
    }
  };

  return (
    <Modal show backdrop="static" onHide={handleClose} centered>
      <Modal.Header className={isEditMode ? 'reedit-mode-wrapper ' : ''} closeButton></Modal.Header>
      <Modal.Body className={isEditMode ? 'reedit-mode-wrapper ' : ''}>
        <FeatureTitleBar title={t('ChangeRegistrationPatternNameModal.head_title')} />
        <Form onSubmit={handleSubmit(onSubmit)}>
          <Row>
            <Col sm={12} className="text-end mt-2">
              <Button type="submit" className="btn--dark-green">
                {t('SaleTypeDetail.save')}
              </Button>
            </Col>
          </Row>
          <div className="sticky-table flex-grow-1 flex-shrink-1 mt-2" style={{ maxHeight: '311px' }}>
            <Table hover>
              <thead>
                <tr>
                  <th>{t('ChangeRegistrationPatternNameModal.Table.Header.pattern_number')}</th>
                  <th>{t('ChangeRegistrationPatternNameModal.Table.Header.pattern_name')}</th>
                  <th>{t('ChangeRegistrationPatternNameModal.Table.Header.version')}</th>
                </tr>
              </thead>
              <tbody>
                {fields.map((field, index) => {
                  return (
                    <tr key={field.id}>
                      <td className="text-center">{field.cd}</td>
                      <td>
                        <Form.Control
                          className="text-center"
                          {...register(`counterPatterns.${index}.name`)}
                          maxLength={255}
                          defaultValue={field.name}
                        ></Form.Control>
                        {errors?.counterPatterns?.[index]?.name && (
                          <span className="small text-danger d-inline-block mt-1">
                            {errors?.counterPatterns?.[index]?.name?.message}
                          </span>
                        )}
                      </td>
                      <td className="text-center">{field.counter_pattern_version ?? '未設定'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </div>
        </Form>
      </Modal.Body>
    </Modal>
  );
}
export default ChangeRegistrationPatternNameModal;
